import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Container,
  useTheme,
  alpha,
  Grid,
  Card,
  CardContent,
  Chip,
  CircularProgress,
} from "@mui/material";
import { LocalOffer } from "@mui/icons-material";
import { POSHeader } from "./";
import OfferServices from "../../../services/OfferService";
import { toast } from "react-toastify";
import ImageLoader from "../../../components/ImageLoader/ImageLoader";

const OffersScreen = ({
  mainTab,
  setMainTab,
  onBack,
  onPOSClick,
  handleTablesClick,
  handleOffersClick,
  onHomeClick,
  onOfferClick,
}) => {
  const theme = useTheme();
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch offers
  useEffect(() => {
    const fetchOffers = async () => {
      try {
        setLoading(true);
        const response = await OfferServices.getAll();

        if (response?.data?.view?.data) {
          setOffers(response.data.view.data);
        }
      } catch (error) {
        console.error("Error fetching offers:", error);
        toast.error("Failed to load offers");
      } finally {
        setLoading(false);
      }
    };

    fetchOffers();
  }, []);

  return (
    <Box
      sx={{
        height: "100%",
        bgcolor: "#f5f5f5",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <POSHeader
        mainTab={mainTab}
        setMainTab={setMainTab}
        onPOSClick={onPOSClick}
        onTablesClick={handleTablesClick}
        onOffersClick={handleOffersClick}
        onHomeClick={onHomeClick}
      />

      {/* Main Content */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          p: 2,
          overflow: "auto",
        }}
      >
        <Container maxWidth="full">
          {/* Loading State */}
          {loading && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                py: 8,
              }}
            >
              <CircularProgress size={60} />
            </Box>
          )}

          {/* Empty State */}
          {!loading && offers.length === 0 && (
            <Box
              sx={{
                py: 8,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Box
                sx={{
                  width: 120,
                  height: 120,
                  borderRadius: "50%",
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 3,
                }}
              >
                <LocalOffer
                  sx={{
                    fontSize: 60,
                    color: theme.palette.primary.main,
                  }}
                />
              </Box>
              <Typography variant="h6" sx={{ mb: 1 }}>
                No Offers Available
              </Typography>
              <Typography variant="body2" color="text.secondary">
                There are currently no active offers or promotions.
              </Typography>
            </Box>
          )}

          {/* Offers Grid */}
          {!loading && offers.length > 0 && (
            <Grid container spacing={2.5}>
              {offers.map((offer) => (
                <Grid
                  item
                  size={{ xs: 12, sm: 6, md: 4, lg: 3 }}
                  key={offer.id}
                >
                  <Card
                    elevation={0}
                    onClick={() => onOfferClick && onOfferClick(offer.id)}
                    sx={{
                      cursor: "pointer",
                      height: "100%",
                      minHeight: 340,
                      display: "flex",
                      flexDirection: "column",
                      border: `2px solid ${alpha(theme.palette.error.main, 0.3)}`,
                      transition: "all 0.2s ease",
                      position: "relative",
                      overflow: "visible",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: `0 8px 20px ${alpha(theme.palette.error.main, 0.15)}`,
                      },
                    }}
                  >
                    {/* Offer Badge */}
                    <Chip
                      icon={<LocalOffer sx={{ fontSize: 16 }} />}
                      label="OFFER"
                      size="small"
                      sx={{
                        position: "absolute",
                        top: -12,
                        right: 8,
                        bgcolor: theme.palette.error.main,
                        color: "white",
                        fontWeight: 700,
                        fontSize: "0.75rem",
                        height: 24,
                        zIndex: 1,
                        "& .MuiChip-icon": {
                          color: "white",
                        },
                      }}
                    />

                    <CardContent
                      sx={{ flex: 1, display: "flex", flexDirection: "column" }}
                    >
                      <Box
                        sx={{
                          width: "100%",
                          height: 140,
                          bgcolor: alpha(theme.palette.error.main, 0.05),
                          borderRadius: 2,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mb: 2,
                          overflow: "hidden",
                          position: "relative",
                        }}
                      >
                        {offer.image ? (
                          <ImageLoader
                            imagePath={offer.image}
                            width="100%"
                            height={140}
                            alt={offer.name}
                            borderRadius={2}
                          />
                        ) : (
                          <Typography
                            variant="h3"
                            sx={{
                              opacity: 0.2,
                              fontWeight: 700,
                            }}
                          >
                            {offer.name.charAt(0)}
                          </Typography>
                        )}

                        {/* Type Badge */}
                        <Box
                          sx={{
                            position: "absolute",
                            top: 8,
                            left: 8,
                            display: "flex",
                            gap: 0.5,
                          }}
                        >
                          <Chip
                            label={offer.type.toUpperCase()}
                            size="small"
                            sx={{
                              bgcolor: alpha(theme.palette.primary.main, 0.9),
                              color: "white",
                              fontWeight: 600,
                              fontSize: "0.7rem",
                              height: 20,
                            }}
                          />
                        </Box>
                      </Box>

                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 600, mb: 0.5, fontSize: "1rem" }}
                      >
                        {offer.name}
                      </Typography>

                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          mb: 2,
                          flex: 1,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                        }}
                      >
                        {offer.description}
                      </Typography>

                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          pt: 1.5,
                          borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "baseline",
                            gap: 1,
                          }}
                        >
                          <Typography
                            variant="h5"
                            sx={{
                              fontWeight: 700,
                              color: theme.palette.error.main,
                              fontSize: "1.25rem",
                            }}
                          >
                            Rs. {parseFloat(offer.price).toFixed(2)}
                          </Typography>
                        </Box>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontSize: "0.7rem" }}
                        >
                          {offer.OfferItems?.length || 0} item
                          {offer.OfferItems?.length !== 1 ? "s" : ""}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Container>
      </Box>
    </Box>
  );
};

export default OffersScreen;
